import { uuid } from "../utils/Uuid";

/**
 * Defines the payload structure for an event, including event name,
 * optional data, a reply function, and an optional cancellation flag.
 */
type TPayload<T, U> = {
  /** The name of the event */
  eventName: string;
  /** Optional data associated with the event */
  data?: T;
  /** Function to send a reply back */
  reply: (data: U) => void;
  /** Flag to indicate if the event has been cancelled */
  isCancelled?: boolean;
};

/**
 * Defines the structure of a reply payload, including the event name,
 * optional data, and an optional cancellation flag.
 */
type TReplyPayload<U> = {
  /** The name of the event */
  eventName: string;
  /** Optional data associated with the reply */
  data?: U;
  /** Flag to indicate if the reply has been cancelled */
  isCancelled?: boolean;
};

/**
 * Represents an event handler function that processes an event payload.
 */
type EventHandler<T, U> = (payload: TPayload<T, U>) => Promise<void> | void;

/**
 * EventBus class for managing event-based communication.
 * Supports synchronous and asynchronous event handling,
 * as well as event responses with replies.
 */
export class EventBus {
  /** Queue to store pending events before processing */
  private eventQueue: { name: string; payload: any; transactionId?: string }[] = [];

  /** Map of event handlers, where each event can have multiple handlers */
  private handlers: Map<string, Set<{ callback: EventHandler<any, any>; once: boolean }>> = new Map();

  /** Map to store pending replies waiting for responses */
  private pendingReplies: Map<string, (response: TReplyPayload<any>) => void> = new Map();

  /** Flag to prevent concurrent queue processing */
  private isProcessing = false;

  /**
   * Registers an event handler for a given event name.
   * @param options Object containing event name, handler function, and optional once flag.
   * @returns An object with an `off` method to remove the handler.
   */
  on<T, U>(options: {
    eventName: string;
    handler: EventHandler<T, U>;
    once?: boolean;
  }) {
    const { eventName, handler, once = false } = options;

    if (!this.handlers.has(eventName)) {
      this.handlers.set(eventName, new Set());
    }

    const handlerSet = this.handlers.get(eventName)!;
    const wrappedHandler = { callback: handler, once };
    handlerSet.add(wrappedHandler);

    return {
      /** Removes the registered event handler */
      off: () => {
        handlerSet.delete(wrappedHandler);
        if (handlerSet.size === 0) this.handlers.delete(eventName);
      }
    };
  }

  /**
   * Emits an event with optional data, with or without expecting a reply.
   * Supports both synchronous and asynchronous event handling.
   */
  // Overload for `withReply: true`
  emit<T, U>(options: { eventName: string; data?: T; withReply: true; timeout?: number; defaultCancellationValue?: U }): Promise<TReplyPayload<U>>;

  // Overload for `withReply: false` or omitted
  emit<T, U>(options: { eventName: string; data?: T; withReply?: false }): void;

  /**
   * Real implementation of the `emit` method.
   * @param options Event options, including name, optional data, reply flag, timeout, and default cancellation value.
   * @returns A promise resolving to the reply payload (if `withReply` is `true`), or void.
   */
  async emit<T, U>(options: {
    eventName: string;
    data?: T;
    withReply?: boolean;
    timeout?: number;
    defaultCancellationValue?: U;
  }): Promise<TReplyPayload<U> | void> {
    const { eventName, data, withReply = false, timeout, defaultCancellationValue } = options;

    if (!withReply) {
      this.eventQueue.push({ name: eventName, payload: data });
      this.processQueue();
      return;
    }

    return new Promise<TReplyPayload<U>>((resolve) => {
      const transactionId = uuid();

      const timeoutId = timeout
        ? setTimeout(() => {
          if (this.pendingReplies.has(transactionId)) {
            this.pendingReplies.get(transactionId)!({
              eventName,
              data: defaultCancellationValue,
              isCancelled: true,
            });
            this.pendingReplies.delete(transactionId);
          }
        }, timeout)
        : null;

      this.pendingReplies.set(transactionId, (response) => {
        if (timeoutId) clearTimeout(timeoutId);
        this.pendingReplies.delete(transactionId);
        resolve(response);
      });

      this.eventQueue.push({ name: eventName, payload: data, transactionId });
      this.processQueue();
    });
  }

  /**
   * Processes the event queue, executing registered handlers sequentially.
   * Ensures events are handled in order and prevents concurrent processing.
   */
  private async processQueue() {
    if (this.isProcessing) return;
    this.isProcessing = true;

    while (this.eventQueue.length > 0) {
      const { name, payload, transactionId } = this.eventQueue.shift()!;
      const eventHandlers = this.handlers.get(name);
      if (!eventHandlers) continue;

      for (const { callback, once } of Array.from(eventHandlers)) {
        queueMicrotask(() =>
          callback({
            eventName: name,
            data: payload,
            reply: (response) => {
              if (transactionId && this.pendingReplies.has(transactionId)) {
                this.pendingReplies.get(transactionId)!({
                  eventName: name,
                  data: response,
                  isCancelled: false,
                });
                this.pendingReplies.delete(transactionId);
              }
            },
          })
        );

        if (once) eventHandlers.delete({ callback, once });
      }

      if (eventHandlers.size === 0) this.handlers.delete(name);
    }

    this.isProcessing = false;
  }
}

export const _eventBus = new EventBus()